import type { TitleRiskVerdict } from '@federa/types/operation-args-title-order';

/** The account-facet labels, grouped rather than spelled flat (`QUAL-CONST-GROUP-001`). */
export const LABEL_ACCOUNT = {
	NAME: 'Account name',
	TYPE: 'Account Type',
	ID: 'Account ID',
	STATUS: 'Account Status',
} as const;

export const LABEL_ACREAGE = 'Acreage';

export const LABEL_ASK = 'Ask';

export const LABEL_ASSESSMENT = 'Assessment';

export const LABEL_BANK_NAME = 'Bank name';

export const LABEL_CLOSE_BY = 'Close by';

export const LABEL_CLOSING_COSTS = 'Closing costs';

/** The deposit REQUIREMENT expressed as a percentage of the bid -- the admin transaction hero tile and the
 *  market-maker detail row, both of which render a `%`-suffixed number beside it. Renamed off the bare
 *  `LABEL_DEPOSIT` name it used to hold, which lied about its `%`: the bare deposit-AMOUNT word is a different
 *  label with eight live sites, and leaving the percentage parked on the unqualified name is what kept them bare. */
export const LABEL_DEPOSIT_PERCENT = 'Deposit %';

/** The SECURITY-deposit percentage stat label -- the one cell BOTH admin transaction quick-stats rows (selling and
 *  buying) end on, rendered beside {@link LABEL_DEPOSIT_PERCENT} in the same stats array while it alone stayed a bare
 *  literal. A PEER of {@link LABEL_DEPOSIT_PERCENT}, not a composition of {@link LABEL_SECURITY_DEPOSIT_SHORT}: that
 *  const is the four offer TABLES' column-head abbreviation, and composing this label off it would make a re-wording
 *  of a table head silently re-word a hero stat. Same reason `LABEL_DEPOSIT_PERCENT` is not composed off
 *  {@link LABEL_DEPOSIT}.
 *
 *  THIS FAMILY STAYS FLAT, AND THE SHAPE IS A MEASURED FORECLOSURE -- the LABEL_SECURITY family (this const,
 *  {@link LABEL_SECURITY_DEPOSIT}, {@link LABEL_SECURITY_DEPOSIT_SHORT}) and the LABEL_DAYS_TO family at
 *  {@link LABEL_DAYS_TO_CLOSE} alike. `QUAL-JSX-ATTR-CONST-ADOPTION-001` (ACTIVE, HIGH) indexes the const tree with a
 *  line-anchored `export const NAME = 'value';` scan and composes its candidate name as `LABEL_<upperSnake(value)>`, so
 *  a grouped member line declares no such export and folding a family DELETES its values from that rule's adoption
 *  index. Measured 2026-08-29 as a two-state control over a probe carrying `label="Days to Close"`,
 *  `label="Security deposit"` and an UNGROUPED `label="Reserve"`: THREE rows with both families flat, ONE folded --
 *  the `LABEL_RESERVE` control alone, which proves the instrument still measured rather than went dark. The grouping
 *  rule enforces this at the declaration (`isLabelCopyAdoptionScalarLine`, `const/const-scalar-adoption-index.ts`),
 *  which is where the foreclosure lives rather than here. Table row:
 *  `.claude/skills/mechanical-foreclosures/grouped-const-schema-foreclosure.md`. */
export const LABEL_SECURITY_DEPOSIT_PERCENT = 'Security Dep. %';

/**
 * The bare deposit-AMOUNT word. See {@link LABEL_CLEAR_TITLE} for why this row is homed rather than left inline:
 * it is the SAME vocabulary word in an offer detail sheet, an offer modal, an offer form field, a table column head
 * and an offer info modal across `mfe/offers`, `mfe/trade` and `mfe/navigator`, standing beside
 * {@link LABEL_DAYS_TO_DEPOSIT} and {@link LABEL_SECURITY_DEPOSIT}, which were already homed in those same literals.
 *
 * Distinct from {@link LABEL_DEPOSIT_PERCENT} (a percentage of the bid) and {@link LABEL_DEPOSIT_AMOUNT}
 * ("Deposit amount", the qualified banking-side label).
 */
export const LABEL_DEPOSIT = 'Deposit';

export const LABEL_FAVORITE_PROPERTIES = 'favorite properties';

export const LABEL_FLOW_ID = 'Flow ID';

export const LABEL_GENERATE_CORPORATE_RESOLUTION = 'Generate Corporate Resolution';

/** The DO-storage READ label for the highest-bid property key, interpolated mid-sentence into the load-failure
 *  message `safeGet` raises (`utils/durable-objects/safe-storage.ts`) -- hence the lowercase noun phrase.
 *
 *  RENAMED onto the `STORAGE_READ_` prefix 2026-08-25: `const/spa-market-maker-values.ts` owns
 *  `LABEL_HIGHEST_BID_FIELD` for the
 *  Title-Case UI stat label `'Highest Bid'`, and two const/ leaves owning one name is what
 *  QUAL-CONST-DUPLICATE-EXPORT-NAME-001 fires on -- both are reached as `@federa/const/<topic>`, so an author who
 *  grepped the name could bind a diagnostic fragment into an `InfoLine` and it would still type-check. */
export const STORAGE_READ_LABEL_HIGHEST_BID = 'highest bid';

export const LABEL_NOT_REVIEWED = 'Not reviewed';

export const LABEL_RETRY = 'Retry';

export const LABEL_SYNC_TO_DO = 'Sync to DO';

export const LABEL_TAX_YEAR = 'Tax year';

export const LABEL_TAXES = 'Taxes';

/**
 * The title-risk verdict CHIP CAPTIONS, grouped rather than spelled flat (`QUAL-CONST-GROUP-001`) and keyed by the
 * verdict each captions. {@link LABEL_NOT_REVIEWED} above is the FOURTH arm of the very same two palettes and has
 * always been spelled here, which is what makes captioning the other three off the enum the odd spelling rather than
 * this record the new one.
 *
 * A VALUE-IDENTICAL TWIN OF `TITLE_RISK_VERDICT` (`const/title-order-enums.ts`) AND NOT A FOLD CANDIDATE -- the
 * structurally-necessary-twin decline, recorded here so a later dedup pass reads the reason instead of re-deriving it.
 * That record's own docstring says what it is: the canonical `title_order.title_risk_verdict` CHECK-IN values,
 * mirroring the live-schema `CHECK (title_risk_verdict IN ('GREEN', 'YELLOW', 'RED') ...)` clause. It is therefore a
 * MIGRATION decision, and a widen of that clause -- to lowercase, or to a longer vocabulary -- is a data-model change
 * that must not silently re-caption an operator-facing banner. Folding the two would restore exactly the coupling the
 * two consumers below were split off from; the strings coinciding today is the coincidence, not the contract.
 *
 * BOUND ON ITS KEYS, NOT ITS VALUES, for that same reason: `Record<TitleRiskVerdict, string>` makes a verdict with no
 * caption, or a caption for no verdict, a compile error, while each caption stays a free string a re-worded banner can
 * change without touching the column vocabulary.
 */
export const LABEL_TITLE_RISK_VERDICT = {
	GREEN: 'GREEN',
	YELLOW: 'YELLOW',
	RED: 'RED',
} as const satisfies Record<TitleRiskVerdict, string>;

export const LABEL_VERIFY_CLOSE_MM_DEAL = 'Verify & close MM deal';

/** The MM Phase-2 notarized-upload step captions, homed on the `LABEL_VERIFY_CLOSE_MM_DEAL` precedent directly above:
 *  the SAME two consumers spell each of them, `flows/mm-progress-state.ts` as the progress step's `label` and
 *  `spa/market-maker/.../page.progress.phases.tsx` as the matching row's `openLabel`. The flow OWNS the phase state and
 *  the page re-typed its captions, so an operator reading the tracker and the operator clicking the row must be reading
 *  one decision. TWO DISTINCT NOTARIZED DOCUMENTS, never folded into one caption -- the page's own comment says so, and
 *  the Certification of Trust is reusable across every Transfer Out while the Trust Agreement is re-routed to the buyer
 *  at close. The `submitLabel` beside each row is title-cased and is a DIFFERENT string; it is not homed here. */
export const LABEL_UPLOAD_NOTARIZED_TRUST_AGREEMENT = 'Upload notarized Trust Agreement';

/** @see LABEL_UPLOAD_NOTARIZED_TRUST_AGREEMENT -- its Certification-of-Trust twin, same two consumers. */
export const LABEL_UPLOAD_NOTARIZED_CERTIFICATION_OF_TRUST = 'Upload notarized Certification of Trust';

export const LABEL_VIEW_PROPERTY = 'View Property';

export const LABEL_AUCTION_STATUS = 'Auction status';

export const LABEL_LIST_PRICE = 'List Price';

/** The ledger SURFACE's name wherever it is rendered to an operator: the admin sidebar entry, the ledger page's top
 *  bar, and the `ledger` detail tab on both the finance-accounts and the admin-users detail panels. It had been a bare
 *  literal at all four.
 *
 *  NOT the same decision as `ACTIVITY_ENTITY_TYPES_LEGACY.LEDGER` (`const/observability-activity-entity-types.ts`),
 *  which spells the same word and must not be folded with this one: that member is a PERSISTED `activity.entity_type`
 *  value, recorded there as a frozen divergence from `TABLES.LEDGER` precisely because historical audit rows already
 *  carry it. Renaming the operator-facing tab must leave every stored row untouched, so the two words move
 *  independently and the collision is coincidental. */
export const LABEL_LEDGER = 'Ledger';

/** The paged ledger's next-page control (`TransactionHistory`). */
export const LABEL_LOAD_MORE = 'Load more';

export const LABEL_MEMO = 'Memo';

export const LABEL_CITY = 'City';

export const LABEL_MODIFIED = 'Modified';

/** Flat with {@link LABEL_DAYS_TO_DEPOSIT} and {@link LABEL_DAYS_TO_CANCEL}, for the adoption-index foreclosure recorded
 *  at {@link LABEL_SECURITY_DEPOSIT_PERCENT}. */
export const LABEL_DAYS_TO_CLOSE = 'Days to Close';

export const LABEL_TRANSACTIONS = 'Transactions';

export const LABEL_COUNTY = 'County';

export const LABEL_MLS_ID = 'MLS ID';

export const LABEL_OFFER_AMOUNT = 'Offer Amount';

export const LABEL_OWNER = 'Owner';

export const LABEL_ZONING = 'Zoning';

export const LABEL_DAYS_TO_DEPOSIT = 'Days to Deposit';

export const LABEL_NOTES = 'Notes';

export const LABEL_PROPERTY_TYPE = 'Property Type';

export const LABEL_STRUCTURE = 'Structure';

/** About-tab section headings that sit beside `LABEL_STRUCTURE` and `LABEL_TAXES` on both navigator surfaces. NOT read
 *  off `NAVIGATOR_OPTIONAL_STEPS`: the step leaf's label for the same id diverges (`Taxes & Zoning` vs `Taxes`), so the
 *  builder-step wording and the About-tab wording are two decisions and folding them would drag the divergence in. */
export const LABEL_UTILITIES = 'Utilities';

export const LABEL_COMMUNITY = 'Community';

export const LABEL_YEAR_BUILT = 'Year Built';

/** Homed here, not on a surface-named leaf: the admin title-snapshot form and the navigator community step render the
 *  same field label, so the decision is not the navigator's. */
export const LABEL_HOA_NAME = 'HOA Name';

/** Homed here, not on a surface-named leaf: the market-maker detail rows and the explore tax-history grid render the
 *  same column heading. */
export const LABEL_TAX_ASSESSMENT = 'Tax Assessment';

/** Homed here, not on a surface-named leaf: the host property-details tab grid, the navigator about-section and the
 *  shared `components/property` fact-icon map all render the same field label. */
export const LABEL_ANNUAL_TAX = 'Annual tax';

export const LABEL_ZIP = 'Zip';

export const LABEL_ACTIONS = 'Actions';

export const LABEL_APPROVAL_LIMIT = 'Approval Limit';

export const LABEL_LEGAL_DESCRIPTION = 'Legal description';

export const LABEL_MODIFYING_FLOW = 'Modifying Flow';

export const LABEL_PAYOUT_ACCOUNT = 'Payout Account';

export const LABEL_USER_TYPE = 'User Type';

export const LABEL_ACCOUNTS = 'Accounts';

/**
 * THE DETAIL-TAB / NAV ROW MEMBERS that repeat across `apps/**`, homed beside the siblings that already were.
 *
 * EVERY `TAB_DEFS` array in `apps/admin-federa` was HALF homed -- `{ key: 'details', label: 'Details' }` beside
 * `{ key: 'dates', label: LABEL_DATES }` in the same literal, four arrays over -- which is the shape
 * {@link LABEL_CLEAR_TITLE} records as how a vocabulary drifts. Only the members with more than one live spelling are
 * homed; a genuinely single-use tab label stays inline rather than becoming one-const-per-tab inventory.
 */
export const LABEL_DETAILS = 'Details';

/** See {@link LABEL_DETAILS}. Also the sidebar/nav entry in both apps, which is the SAME vocabulary word. */
export const LABEL_OFFERS = 'Offers';

/** See {@link LABEL_DETAILS}. The `PAGE_ROUTES.EXPLORE` nav entry, named ONCE for the two chromes that both render it
 *  in `apps/federa`: the desktop `Sidebar` `primaryNavItems` row and the `MobileBottomNav` tab. The pair is the exact
 *  half-homed shape this family records -- `Sidebar` already read {@link LABEL_OFFERS} and {@link LABEL_TRANSACTIONS}
 *  from here while spelling its FIRST item inline, and the mobile chrome spelled the same word a second time, so a
 *  rename of the destination would have had to be found in two files. */
export const LABEL_EXPLORE = 'Explore';

/** See {@link LABEL_DETAILS}. Bare status, distinct from `LABEL_ACCOUNT.STATUS`, `LABEL_AUCTION_STATUS` and `LABEL_PARTNER_STATUS`, which are qualified. */
export const LABEL_STATUS = 'Status';

export const LABEL_ACTIVITY = 'Activity';

export const LABEL_ADDRESS_LINE_1 = 'Address Line 1';

export const LABEL_CI_CD = 'CI/CD';

export const LABEL_COUNTRY = 'Country';

export const LABEL_DATES = 'Dates';

export const LABEL_DEPOSIT_AMOUNT = 'Deposit amount';

export const LABEL_FULL_ADDRESS = 'Full Address';

export const LABEL_PARTNER_STATUS = 'Partner Status';

export const LABEL_PREV = 'Prev';

export const LABEL_PROGRESS = 'Progress';

export const LABEL_RESOLVED = 'Resolved';

export const LABEL_SELLER = 'Seller';

export const LABEL_SMART_AGENT_STATUS = 'SmartAgent Status';

export const LABEL_SOURCE_URL = 'Source URL';

export const LABEL_WINNING_BID = 'Winning bid';

/**
 * THE PROPERTY-FACT LABEL ROW -- the five members of `buildPropertyFacts` that had no const, beside the three that
 * already did (`LABEL_ASK`, `LABEL_CLOSE_BY`, `LABEL_CLOSING_COSTS`).
 *
 * THIS LABEL IS A JOIN KEY, not decoration. The same string appears as a `PropertyFact.label`, as the lookup key into
 * `PROPERTY_FACT_HELP_TEXT_MAP` (`apps/federa/src/components/property/PropertyFacts.helpers.ts`) and, for the deposit,
 * as an `autoShrink` equality test in `AuctionTermsCard`. Nothing fails when the spellings drift -- the map lookup just
 * returns `undefined` and the info affordance silently disappears -- and the file had ALREADY drifted against itself:
 * the help map keyed `Ask` and `'Closing costs'` as bare literals while the facts array pushed `LABEL_ASK` and
 * `LABEL_CLOSING_COSTS` for the same two rows. Half a vocabulary homed is how that happens.
 */
export const LABEL_CLEAR_TITLE = 'Clear title';

/** See {@link LABEL_CLEAR_TITLE} -- also compared by `AuctionTermsCard`'s `autoShrink` test. */
export const LABEL_SECURITY_DEPOSIT = 'Security deposit';

/** The ABBREVIATED column head for the same field, for the offer TABLES whose column has no room for the full label --
 *  the admin offers section table, the offers MFE's offer table, the navigator's my-offers table and the market-maker
 *  detail table. A SEPARATE decision from {@link LABEL_SECURITY_DEPOSIT}, not a variant of it: the abbreviation and its
 *  trailing period are a typographic choice those four tables must make identically, and four hand-typed copies of
 *  "Security Dep." is how one of them ends up as "Sec. Deposit". */
export const LABEL_SECURITY_DEPOSIT_SHORT = 'Security Dep.';

/** See {@link LABEL_CLEAR_TITLE}. Distinct from `LABEL.DAYS_TO_CANCEL` (`const/apps-admin-federa-values.ts`), which is the admin table's title-cased column head. */
export const LABEL_DAYS_TO_CANCEL = 'Days to cancel';

/** See {@link LABEL_CLEAR_TITLE}. */
export const LABEL_RESERVE = 'Reserve';

/** See {@link LABEL_CLEAR_TITLE}. */
export const LABEL_SHOWINGS = 'Showings';

/**
 * The words that name a money-amount field to the user: the `CurrencyInput` primitive's DEFAULT placeholder -- what
 * every caller that supplies none inherits -- and the top-up modal's visible `<label>`, which hand-rolls its own
 * amount field out of `Input` rather than using that primitive. Two spellings of one prompt, and the modal's copy is
 * the one that silently stops matching the rest of the product when the primitive's default is reworded.
 */
export const LABEL_ENTER_AMOUNT = 'Enter amount';

/**
 * THE UNQUALIFIED FIELD-LABEL ROW -- the bare one-word heads that the detail rows, table header arrays and filter
 * configs across `apps/**` each spelled by hand, beside the qualified siblings above that already had a const.
 *
 * {@link LABEL_STATUS} is the shape: it has been homed here since the detail-tab pass, and admin-federa still carried
 * SIXTEEN bare `'Status'` spellings against it -- `<StatusRow label="Status">`, `{ key: 'status', header: 'Status' }`
 * and `headerCells(..., 'Status', ...)` -- in the SAME files that already imported `LABEL_MEMO` and
 * `LABEL_SECURITY_DEPOSIT` from this leaf. Half a vocabulary homed is how a table head and the detail row for the same
 * column drift apart, which is exactly what {@link LABEL_CLEAR_TITLE} records.
 *
 * These are the field-label decision, NOT a value vocabulary: `LABEL_DEBIT`/`LABEL_CREDIT` are the money COLUMN heads
 * and are deliberately separate from `LEDGER_ENTRY_TYPE` (`const/ledger-correction-vocabulary.ts`), whose identically
 * spelled members mirror the `ledger_correction_requests.entry_type` CHECK and must track the migration, not the UI.
 */
export const LABEL_AMOUNT = 'Amount';

/** See {@link LABEL_AMOUNT}. */
export const LABEL_CATEGORY = 'Category';

/** See {@link LABEL_AMOUNT}. */
export const LABEL_CREATED = 'Created';

/** See {@link LABEL_AMOUNT}. The ledger money-column head, NOT `LEDGER_ENTRY_TYPE.CREDIT`. */
export const LABEL_CREDIT = 'Credit';

/** See {@link LABEL_AMOUNT}. */
export const LABEL_DATE = 'Date';

/** See {@link LABEL_AMOUNT}. The ledger money-column head, NOT `LEDGER_ENTRY_TYPE.DEBIT`. */
export const LABEL_DEBIT = 'Debit';

/** See {@link LABEL_AMOUNT}. */
export const LABEL_EMAIL = 'Email';

/** See {@link LABEL_AMOUNT}. Distinct from `LABEL_ACCOUNT.NAME` and {@link LABEL_BANK_NAME}, which are qualified. */
export const LABEL_NAME = 'Name';

/** See {@link LABEL_AMOUNT}. The payout/ledger method column head, rendered by both the admin-finance payouts grid and
 *  the refund-batch preview grid, so the decision is not either surface's. */
export const LABEL_METHOD = 'Method';

/** See {@link LABEL_AMOUNT}. The bare column head the navigator funding rows and the admin-finance ledger and
 *  refund-batch grids share. Distinct from `LABEL_REFERENCE_ID` (`const/mfe-admin-finance-label-values.ts`), the
 *  qualified 'Reference ID' spelling that only the admin-finance payout surfaces render. */
export const LABEL_REFERENCE = 'Reference';

/** See {@link LABEL_AMOUNT}. */
export const LABEL_NICKNAME = 'Nickname';

/** See {@link LABEL_AMOUNT}. */
export const LABEL_PHONE = 'Phone';

/** See {@link LABEL_AMOUNT}. The address facet, beside {@link LABEL_CITY}, {@link LABEL_COUNTY} and {@link LABEL_ZIP}. */
export const LABEL_STATE = 'State';

/** See {@link LABEL_AMOUNT}. The caption for an offer's `createdAt` field -- the offers MFE's table column head, its
 *  offer-detail date row, and the navigator's my-offers table column head over the same `created_at`, which must agree
 *  because they are one row seen from three routes. NOT
 *  `OFFER_STATUSES.SUBMITTED` (`const/offers.ts`): that record mirrors the `offer_status` CHECK and drives the
 *  transition table, so binding the caption to it would let a status rewording silently rename a DATE column. */
export const LABEL_SUBMITTED = 'Submitted';

/** See {@link LABEL_AMOUNT}, whose "field-label decision, NOT a value vocabulary" split this member is a second
 *  witness for. The caption for the success-manager FIELD wherever it is named: the admin-users advanced filter
 *  (`filter_success_manager`, beside {@link LABEL_CITY} and {@link LABEL_STATE} in the same array) and the
 *  user-profile detail card and edit form over `success_manager` itself
 *  (`components/user-profile/user-detail-sections.tsx`, `components/user-profile/user-detail-edit.tsx`). NOT
 *  `TEAM_TYPES.SUCCESS` (`const/flow-engine-status.ts`): that record is the `team_type` COLUMN vocabulary -- every
 *  other consumer writes it as a row value (`team_type: TEAM_TYPES.FINANCE`) -- so a team rename is a data-model edit
 *  that would silently reword this caption. NOT `CLAIM_ROLES.SUCCESS_MANAGER` (`const/auth.ts`)
 *  either, which is the wire role `'success_manager'`. Three vocabularies, one job title, one decision each. */
export const LABEL_SUCCESS_MANAGER = 'Success Manager';

/** See {@link LABEL_AMOUNT}. Distinct from `LABEL_ACCOUNT.TYPE`, {@link LABEL_PROPERTY_TYPE} and {@link LABEL_USER_TYPE}, which are qualified. */
export const LABEL_TYPE = 'Type';

/**
 * THE NAVIGATOR STEP-LABEL ROW -- the six builder-step headings `const/navigator-steps.ts` spelled inline while the
 * REVIEW screen that re-renders every one of them (`components/navigator/steps/ReviewStepSections.sections.tsx`)
 * spelled them a second time by hand, beside the four siblings on the SAME screen that already read
 * {@link LABEL_DETAILS}, {@link LABEL_COMMUNITY}, {@link LABEL_SHOWINGS} and {@link LABEL_UTILITIES} from this leaf.
 * Half a vocabulary homed is the shape {@link LABEL_CLEAR_TITLE} records, and here the two halves are the step the
 * user fills in and the summary card that names it back to them -- a divergence a reviewer sees as two names for one
 * step.
 *
 * NAMED FOR THE VALUE, not for the navigator: `QUAL-JSX-ATTR-CONST-ADOPTION-001` composes its candidate as
 * `LABEL_<upperSnake(value)>`, so a surface-named spelling is a const the matcher can never reach.
 */
export const LABEL_ADDRESS = 'Address';

/** See {@link LABEL_ADDRESS}. Distinct from {@link LABEL_LEGAL_DESCRIPTION}, which is the title-report field. */
export const LABEL_DESCRIPTION = 'Description';

/** See {@link LABEL_ADDRESS}. */
export const LABEL_PRICING = 'Pricing';

/**
 * See {@link LABEL_ADDRESS}. Also the admin table column head, previously spelled a third time as
 * `LABEL.LISTING_TYPE` in `const/apps-admin-federa-values.ts` -- that member now READS this const rather than
 * respelling it, because a shared `components/` screen may not depend on an app-scoped record.
 */
export const LABEL_LISTING_TYPE = 'Listing Type';

/** The property-id field label, on the {@link LABEL_LISTING_TYPE} precedent: the market-maker detail rows print it and
 *  `LABEL.PROPERTY_ID` in `const/apps-admin-federa-values.ts` READS it, since that record is app-scoped. */
export const LABEL_PROPERTY_ID = 'Property ID';

/** See {@link LABEL_ADDRESS}. The ampersand folds to `_` when the candidate const name is composed, which is why the
 *  name reads LAND_LOT rather than LAND_AND_LOT -- neither is a symbol, both are name fragments of {@link LABEL_LAND_LOT}. */
export const LABEL_LAND_LOT = 'Land & Lot';

/** See {@link LABEL_ADDRESS}. The BUILDER-step wording; deliberately NOT composed off {@link LABEL_TAXES}, which is the
 *  About-tab heading for the same id and is a separate decision (see {@link LABEL_UTILITIES}). */
export const LABEL_TAXES_ZONING = 'Taxes & Zoning';

/**
 * THE SESSION-ACTION ROW -- the words a control uses to start and end a session, homed 2026-09-02 across the three
 * chromes that render them: the profile SPA's own sidebar and mobile sheet (`spa/profile/frontend/`), the shared
 * sidebar user menu (`components/layout/SidebarUserMenu.tsx`) and the host app's mobile drawer
 * (`apps/federa/src/components/layout/MobileMenuDrawer.menu-data.tsx`). Three packages with no shared non-const leaf
 * between them, so the `const/` floor is the only home all three can reach.
 *
 * TWO SPELLINGS OF ONE DECISION, AND THAT IS RECORDED HERE RATHER THAN RESOLVED HERE. `Log out` and `Logout` name the
 * same action, as do `Log in` and `Login`; nothing in the tree argues the difference, and the sharpest evidence that it
 * is drift rather than design sits four lines apart inside ONE component -- `UserMenuDropdown` in that menu renders the
 * two arms of a single signed-in toggle as `Logout` (noun) and `Log in or sign up` (verb), from the same file, into the
 * same menu. Homing both spellings is what MAKES the eventual unification a one-line change instead of a six-site grep,
 * which is the whole reason the losing spelling gets a const rather than being left inline.
 *
 * WHY THE UNIFICATION IS NOT TAKEN IN THIS CHANGE: picking a winner is a product-copy decision the tree does not
 * settle. The count is a tie (`Log out` and `Logout` at two sites each), and the product's dominant voice for this
 * domain is neither of them -- roughly twenty sites say `Sign in` (every SPA login subtitle, the login-modal title,
 * `ERROR_MESSAGES.REFUND_REQUEST_SIGN_IN_REQUIRED`, `PROFILE_COPY.authError`), so a fold onto either button spelling
 * would standardise on a third vocabulary while the rest of the product speaks a fourth.
 *
 * FLAT SCALARS, NOT A GROUPED RECORD, and the shape is forced rather than stylistic: `QUAL-JSX-ATTR-CONST-ADOPTION-001`
 * indexes the const tree with a line-anchored `export const NAME = 'value';` scan and composes its candidate as
 * `LABEL_<upperSnake(value)>`, so a grouped member declares no export that matcher can reach and folding these five
 * into a record would delete them from its adoption index -- the foreclosure {@link LABEL_SECURITY_DEPOSIT_PERCENT}
 * records for the LABEL_SECURITY family (a name PREFIX, unbackticked: no export is spelled it), one carrier over.
 */
export const LABEL_LOG_IN = 'Log in';

/**
 * A sign-in gate's heading for one product: the act in the verb the button under it carries, then the product's name.
 * @runtime-derived-ssot-helper composes the same-file {@link LABEL_LOG_IN}, so every product's gate says the act the
 * same way and a change of verb reaches all of them.
 */
export function logInTo(productName: string): string {
	return `${LABEL_LOG_IN} to ${productName}`;
}

/** See {@link LABEL_LOG_IN}. The profile SPA's spelling of the session-ending action, at its desktop dropdown and its
 *  mobile sheet -- the two chromes of ONE shell, which is why they may not drift from each other. */
export const LABEL_LOG_OUT = 'Log out';

/** See {@link LABEL_LOG_IN}. The host app's spelling of the SAME action as {@link LABEL_LOG_OUT}, at its desktop
 *  sidebar menu and its mobile drawer -- one app at two breakpoints, byte-identical today and homed so it stays that
 *  way. Adopting it is byte-neutral; the open question is which of the two spellings the product keeps, not whether
 *  this site should read a const. */
export const LABEL_LOGOUT = 'Logout';

/** See {@link LABEL_LOG_IN}. The signed-out arm of the SAME drawer ternary as {@link LABEL_LOGOUT}. Homed because
 *  `QUAL-JSX-ATTR-CONST-ADOPTION-001`'s own fix prose requires both arms of a ternary to be adopted or neither, so one
 *  arm does not read as a const while its twin stays a literal -- not because a single site earns a const on its own. */
export const LABEL_LOGIN = 'Login';

/**
 * THE SAVE-A-PROPERTY BOOKMARK VOCABULARY -- the words the bookmark toggle announces, homed 2026-09-04 across the two
 * `components/` surfaces that spell them: the per-icon ARIA table (`components/icons/semantic-tokens.ts`, the
 * `bookmark` / `bookmark-solid` metas) and the toggle button itself (`components/common/SaveBookmarkButton.tsx`,
 * which had them as two file-private `SAVED_LABEL` / UNSAVED_LABEL scalars). Two packages' worth of one decision:
 * the icon's accessible name and the button's accessible name are the SAME announcement, and the button renders that
 * very icon.
 *
 * THIS IS THE ACCESSIBLE NAME OF AN ICON-ONLY CONTROL, which is why it is a contract and not decoration.
 * `SaveBookmarkButton` emits no text node, so `aria-label` is the entire accessible name -- its suite pins the two
 * states as announcing DIFFERENTLY, because a bookmark that reads the same before and after pressing is unusable. A
 * reword that reaches one spelling and not the other is exactly the failure `components/domain-cards/__tests__/
 * PropertyCard.details.test.tsx` records: a filled bookmark still announcing "Save property", which no sighted
 * reviewer can see.
 *
 * {@link LABEL_UNSAVE_PROPERTY} IS HOMED THOUGH IT HAS ONE SITE, on the {@link LABEL_LOGIN} ground one carrier over:
 * it is the paired arm of {@link LABEL_SAVE_PROPERTY} in the same icon table, and half a vocabulary homed is the
 * drift shape {@link LABEL_CLEAR_TITLE} records. It is not a const earned by a single site on its own.
 *
 * MINTING THESE IS THE ENFORCEMENT, not a preliminary to it. `QUAL-JSX-ATTR-CONST-ADOPTION-001` (ACTIVE, HIGH) reads
 * the const tree through the injected scanner fs at match time and gates its property arm on the rendered-text names,
 * of which `ariaLabel` is one -- so the icon table's `ariaLabel: '<value>'` members become convictable the moment
 * these exports exist, with no edit to that rule. FLAT SCALARS for the same reason its statusReason states: the rule
 * indexes only line-anchored `export const NAME = 'value';`, so a grouped record would delete these three values from
 * its adoption index.
 *
 * THE HOST APP'S JSX SITE IS DELIBERATELY LEFT LITERAL -- `aria-label={isSaved ? 'Manage saved lists' : 'Save
 * property'}` in `apps/federa/src/app/property/[propertyId]/PropertyPageClient.components.tsx`. `aria-label` is
 * LINT-ANCHORED (`const/jsx-lint-anchored-attrs.ts`): the `jsx-a11y` family reads those literals, so a const
 * reference there takes that matcher dark rather than curing anything, which is the measured `rel="noopener
 * noreferrer"` regression that leaf exists to record. Adopting inside `SaveBookmarkButton`'s own `aria-label` is NOT
 * that case and costs nothing -- both arms were ALREADY identifiers there, so the linter was already blind, and this
 * change only swaps a file-private name for the shared one.
 */
export const LABEL_SAVE_PROPERTY = 'Save property';

/** See {@link LABEL_SAVE_PROPERTY}. The FILLED bookmark's announcement in the shared icon table -- the arm that must
 *  move whenever its twin does. Distinct from {@link LABEL_MANAGE_SAVED_LISTS}: this names UNDOING the save, which is
 *  what the icon alone can offer, while the button in the host app opens a list picker instead. */
export const LABEL_UNSAVE_PROPERTY = 'Unsave property';

/** See {@link LABEL_SAVE_PROPERTY}. The SAVED-state arm of `SaveBookmarkButton`'s ternary, and a different action from
 *  {@link LABEL_UNSAVE_PROPERTY} rather than a rewording of it -- pressing a saved bookmark here opens the saved-lists
 *  manager, so the two are not interchangeable and folding them would mis-announce the control. */
export const LABEL_MANAGE_SAVED_LISTS = 'Manage saved lists';

/**
 * THE SURVIVING-LIEN TOTAL'S CAPTION -- one money cell named identically to two audiences, homed 2026-09-04.
 *
 * It was BUYER_TITLE_REPORT_COPY.SUMMARY_SURVIVING_LABEL in `const/title-instrument-labels.ts` -- unbackticked because
 * that member left with this move and only its `SUMMARY_*` siblings remain there -- and that carrier
 * NAMES AN AUDIENCE the second consumer is not: the auction-manager title panel
 * (`mfe/admin-auctions/frontend/AuctionManagerApp.title-info.tsx`) captions the very same
 * `title_order.surviving_lien_total_usd` figure for an OPERATOR, and it spelled the words by hand rather than reach
 * into a record whose docstring scopes it to "the buyer report's section headings and blurbs". A member an operator
 * surface must read cannot live under a `BUYER_` prefix without the name lying, so the decision moves to this leaf and
 * the buyer record keeps only what is genuinely the buyer report's voice.
 *
 * THE TWO CELLS ARE ONE DECISION, not two that coincide. Both caption the SAME examiner-produced total -- the figure a
 * bid is priced against -- and the buyer card's own docstring makes that lead cell "the examiner's determination". An
 * operator reading one name for the number and the bidder they are talking to reading another is the failure, and it
 * is invisible from either screen alone.
 *
 * DISTINCT FROM the three LONGER spellings that qualify it for their own context and stay inline as single-site copy:
 * `'Surviving liens (USD)'` and `'Surviving liens (buyer takes subject to)'` in the admin title-report screens, and
 * `'Surviving liens the buyer inherits'` in `components/title-report/polished-report-sections-overview.tsx`. Those are
 * sentences about the number; this is its name.
 *
 * A FLAT SCALAR for the reason {@link LABEL_SAVE_PROPERTY} states: `QUAL-JSX-ATTR-CONST-ADOPTION-001` indexes only
 * line-anchored `export const NAME = 'value';`, and the grouped member it used to be was invisible to that adoption
 * index -- which is how the operator panel came to respell it in the first place.
 */
export const LABEL_SURVIVING_LIENS = 'Surviving liens';

/**
 * THE TWO AUCTION DESTINATIONS' NAMES -- the operator console's `Auctions` nav group, homed 2026-09-04 beside the
 * five siblings in that very same nav array that already read this leaf ({@link LABEL_CI_CD}, {@link LABEL_ACTIONS},
 * {@link LABEL_TRANSACTIONS}, {@link LABEL_OFFERS}, {@link LABEL_ACCOUNTS}).
 *
 * EACH HAS A SECOND LIVE SPELLING, which is what earns it a const where the nav's single-use rows do not (the test
 * {@link LABEL_AMOUNT} states, and the reason `Analytics`, `Chats`, `Groups` and `Changelog` stay inline one array
 * over): `apps/admin-federa/src/components/layout/AdminSidebar.tsx` names the destination, and the MFE page it opens
 * TITLES ITSELF with the same words through `AuctionsTopBar` (`mfe/admin-auctions/frontend/active-auctions.tsx` and
 * `my-auctions.tsx`). A rename that reaches the nav and not the page leaves an operator clicking `Active Auctions`
 * and landing on a heading that says something else -- and neither file can see the other.
 *
 * NOT `ADMIN_AUCTION_VIEW` (`const/mfe-auction-views.ts`), whose members are the ROUTE ids `active` / `my` and are
 * already what keys these two nav rows. The id and the words are two decisions: the id is in URLs and in the
 * visibility filter, the words are on a screen.
 *
 * NOT the flow display name `'Active Auctions'` on `flows/admin-active-auctions.ts` either -- flow-registry metadata
 * names a graph for an operator reading the flow catalogue, not a page for an operator reading the console, and
 * folding them would make a nav reword a flow rename.
 */
export const LABEL_ACTIVE_AUCTIONS = 'Active Auctions';

/** See {@link LABEL_ACTIVE_AUCTIONS} -- the sibling destination, nav row and page title, moving as one. */
export const LABEL_MY_AUCTIONS = 'My Auctions';

/** See {@link LABEL_LOG_IN}. The shared sidebar menu's signed-out row, and the heading of every login box that wears a
 *  product's own wordmark (`ownNameApp`, `const/single-sign-on-apps.ts`). NOT a variant spelling of
 *  {@link LABEL_LOG_IN}: it discloses that the same control also REGISTERS, which the bare verb does not, and the row
 *  is styled to wrap (`MENU_ITEM_BUTTON_STYLE` sets `whiteSpace: 'normal'`) precisely so it can carry the longer
 *  sentence. Folding it onto the bare verb would delete a disclosed affordance, so it stays its own decision.
 *
 *  The login box's own default line (`components/auth/components/LoginModal.tsx`) reads this label too: it used to
 *  spell `'Log in or Sign up'`, one character apart, in the box this row OPENS; the wordmark boxes' heading settled the
 *  casing, and the box now says the one spelling. */
export const LABEL_LOG_IN_OR_SIGN_UP = 'Log in or sign up';

/**
 * The ask cell's phrase for a live auction that has no bid yet -- the property page's ask row and the property card's
 * price cell, which are the same sentence about the same state on two surfaces a user reaches one click apart.
 *
 * HOMED 2026-09-05 to pay a debt BOTH carriers had already written down and neither could settle alone.
 * `askValueFor` (`apps/federa/src/app/property/[propertyId]/PropertyPageClient.tsx`) recorded the pair in its own
 * docstring as "one decision with two homes ... owed a `const/` leaf this module cannot mint", while
 * `AUCTION_OR_BEST_OFFER_DISPLAY` (`components/lib/property-card-display.ts`) named its half "single source, not
 * copy-pasted" -- true within that file and false across the two.
 *
 * NO MATCHER OWED IT, which is why it survived two sweeps and is recorded here rather than routed to a rule.
 * `QUAL-CROSS-FILE-PHRASE-CONST-DUP-001` needs BOTH sides to bind the phrase to a named const; here one side binds an
 * OBJECT whose member carries it and the other spells it inline, so neither is that rule's shape. Its inline partner
 * `QUAL-CROSS-FILE-HOMELESS-PHRASE-INLINE-001` needs TWO inline spellings and sees one. The remaining tree hits are
 * all comment and docstring quotations of the rendered text, which every matcher in the family masks.
 *
 * NOT a peer of {@link LABEL_ASK}, the label this same function returns on its other arm: that one is a column
 * CAPTION beside a formatted amount, this is the amount CELL's whole text where no number exists to show. They are
 * rendered in different slots and a reword of either must not move the other.
 */
export const LABEL_AUCTION_OR_BEST_OFFER = 'Auction or Best offer';

// LABELS

export const LABEL_PROFILE = 'Profile';

export const LABEL_GROUPS = 'Groups';

export const LABEL_KNOWLEDGEBASE = 'Knowledgebase';
